import { useState, useEffect } from "react";
import { format, isToday, isTomorrow, differenceInDays, addDays, parseISO, isBefore, startOfDay } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { 
  Search, 
  Calendar as CalendarIcon, 
  Clock, 
  MapPin, 
  Users, 
  Download, 
  Printer, 
  FileDown,
  User,
  ChevronRight,
  Timer,
  BookOpen,
  AlertCircle,
  CheckCircle,
  HelpCircle
} from "lucide-react";
import { toast } from "sonner";
import { SidebarProvider } from "@/components/ui/sidebar";
import Teacherheader from "../Teacherheader";
import Teachersidebar from "../Teachersidebar";

// Types for timetable data
interface Exam {
  id: string;
  name: string;
  code: string;
  date: string;
  slot: string;
  slotTime: string;
  rooms: string[];
  studentCount: number;
  invigilators: string[];
  department?: string;
  notes?: string;
  specialRequirements?: string;
}

interface TimetableData {
  id: string;
  name: string;
  institution: string;
  examPeriod: { start: string; end: string };
  exams: Exam[];
  rooms: string[];
  slots: { name: string; time: string }[];
}

// Sample timetable data for demo
const sampleTimetableData: TimetableData = {
  id: "ABC123",
  name: "Semester 1 Final Exams 2024",
  institution: "Greenwood Academy",
  examPeriod: {
    start: format(addDays(new Date(), 3), "yyyy-MM-dd"),
    end: format(addDays(new Date(), 12), "yyyy-MM-dd"),
  },
  rooms: ["Hall A", "Hall B", "Room 101", "Room 102", "Lab 1"],
  slots: [
    { name: "Morning", time: "09:00 - 12:00" },
    { name: "Afternoon", time: "14:00 - 17:00" },
  ],
  exams: [
    {
      id: "1",
      name: "Mathematics Advanced",
      code: "MATH301",
      date: format(addDays(new Date(), 3), "yyyy-MM-dd"),
      slot: "Morning",
      slotTime: "09:00 - 12:00",
      rooms: ["Hall A", "Hall B"],
      studentCount: 180,
      invigilators: ["Mr. Johnson", "Mrs. Smith", "Dr. Wilson"],
      department: "Mathematics",
      notes: "Calculators allowed",
    },
    {
      id: "2",
      name: "English Literature",
      code: "ENG201",
      date: format(addDays(new Date(), 3), "yyyy-MM-dd"),
      slot: "Afternoon",
      slotTime: "14:00 - 17:00",
      rooms: ["Room 101"],
      studentCount: 45,
      invigilators: ["Mrs. Brown"],
      department: "English",
    },
    {
      id: "3",
      name: "Physics",
      code: "PHY301",
      date: format(addDays(new Date(), 4), "yyyy-MM-dd"),
      slot: "Morning",
      slotTime: "09:00 - 12:00",
      rooms: ["Hall A"],
      studentCount: 90,
      invigilators: ["Dr. Wilson", "Mr. Thompson"],
      department: "Science",
      specialRequirements: "Lab equipment needed",
    },
    {
      id: "4",
      name: "Chemistry",
      code: "CHEM301",
      date: format(addDays(new Date(), 4), "yyyy-MM-dd"),
      slot: "Afternoon",
      slotTime: "14:00 - 17:00",
      rooms: ["Lab 1"],
      studentCount: 60,
      invigilators: ["Mrs. Smith"],
      department: "Science",
      notes: "Safety goggles required",
    },
    {
      id: "5",
      name: "History",
      code: "HIST201",
      date: format(addDays(new Date(), 5), "yyyy-MM-dd"),
      slot: "Morning",
      slotTime: "09:00 - 12:00",
      rooms: ["Room 102"],
      studentCount: 55,
      invigilators: ["Mr. Johnson"],
      department: "Humanities",
    },
    {
      id: "6",
      name: "Biology",
      code: "BIO301",
      date: format(addDays(new Date(), 6), "yyyy-MM-dd"),
      slot: "Morning",
      slotTime: "09:00 - 12:00",
      rooms: ["Hall B"],
      studentCount: 75,
      invigilators: ["Dr. Wilson", "Mrs. Brown"],
      department: "Science",
    },
    {
      id: "7",
      name: "Computer Science",
      code: "CS301",
      date: format(addDays(new Date(), 7), "yyyy-MM-dd"),
      slot: "Afternoon",
      slotTime: "14:00 - 17:00",
      rooms: ["Room 101", "Room 102"],
      studentCount: 85,
      invigilators: ["Mr. Thompson"],
      department: "Technology",
      notes: "Computers provided",
    },
    {
      id: "8",
      name: "Geography",
      code: "GEO201",
      date: format(addDays(new Date(), 8), "yyyy-MM-dd"),
      slot: "Morning",
      slotTime: "09:00 - 12:00",
      rooms: ["Hall A"],
      studentCount: 65,
      invigilators: ["Mrs. Smith", "Mr. Johnson"],
      department: "Humanities",
    },
  ],
};

const TeacherExamViewer = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [timetableData, setTimetableData] = useState<TimetableData | null>(sampleTimetableData);
  const [timetableCode, setTimetableCode] = useState("");
  const [teacherName, setTeacherName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedExam, setSelectedExam] = useState<Exam | null>(null);
  const [showExamModal, setShowExamModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [filterRoom, setFilterRoom] = useState<string>("all");
  const [filterDepartment, setFilterDepartment] = useState<string>("all");
  const [isLoading, setIsLoading] = useState(false);

  // Load teacher name from localStorage
  useEffect(() => {
    const savedName = localStorage.getItem("teacherName");
    if (savedName) setTeacherName(savedName);
  }, []);

  // Save teacher name to localStorage
  const saveTeacherName = (name: string) => {
    setTeacherName(name);
    localStorage.setItem("teacherName", name);
    toast.success("Your name has been saved");
  };

  // Handle timetable code submission
  const handleLoadTimetable = () => {
    if (!timetableCode.trim()) {
      toast.error("Please enter a timetable code");
      return;
    }
    setIsLoading(true);
    // Simulate loading
    setTimeout(() => {
      if (timetableCode.toUpperCase() === "ABC123") {
        setTimetableData(sampleTimetableData);
        toast.success("Timetable loaded successfully!");
      } else {
        toast.error("Invalid timetable code. Try 'ABC123' for demo.");
      }
      setIsLoading(false);
    }, 1000);
  };

  // Handle JSON file upload
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target?.result as string);
        setTimetableData(data);
        toast.success("Timetable loaded from file!");
      } catch {
        toast.error("Invalid JSON file format");
      }
    };
    reader.readAsText(file);
  };

  // Get filtered exams
  const getFilteredExams = () => {
    if (!timetableData) return [];
    
    let filtered = [...timetableData.exams];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (exam) =>
          exam.name.toLowerCase().includes(query) ||
          exam.code.toLowerCase().includes(query) ||
          exam.rooms.some((r) => r.toLowerCase().includes(query)) ||
          exam.invigilators.some((i) => i.toLowerCase().includes(query))
      );
    }

    // Room filter
    if (filterRoom !== "all") {
      filtered = filtered.filter((exam) => exam.rooms.includes(filterRoom));
    }

    // Department filter
    if (filterDepartment !== "all") {
      filtered = filtered.filter((exam) => exam.department === filterDepartment);
    }

    // Date filter
    if (selectedDate) {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      filtered = filtered.filter((exam) => exam.date === dateStr);
    }

    return filtered.sort((a, b) => {
      if (a.date !== b.date) return a.date.localeCompare(b.date);
      return a.slot === "Morning" ? -1 : 1;
    });
  };

  // Get teacher's exams
  const getMyExams = () => {
    if (!timetableData || !teacherName) return [];
    const name = teacherName.toLowerCase();
    return timetableData.exams
      .filter((exam) =>
        exam.invigilators.some((inv) => inv.toLowerCase().includes(name))
      )
      .sort((a, b) => {
        if (a.date !== b.date) return a.date.localeCompare(b.date);
        return a.slot === "Morning" ? -1 : 1;
      });
  };

  // Get next exam countdown
  const getNextExam = () => {
    if (!timetableData) return null;
    const today = startOfDay(new Date());
    const upcomingExams = timetableData.exams
      .filter((exam) => !isBefore(parseISO(exam.date), today))
      .sort((a, b) => a.date.localeCompare(b.date));
    return upcomingExams[0] || null;
  };

  // Get unique departments
  const getDepartments = () => {
    if (!timetableData) return [];
    const depts = new Set(timetableData.exams.map((e) => e.department).filter(Boolean));
    return Array.from(depts) as string[];
  };

  // Export to CSV
  const exportToCSV = (exams: Exam[]) => {
    const headers = ["Date", "Time", "Exam", "Code", "Room(s)", "Students", "Invigilators"];
    const rows = exams.map((exam) => [
      format(parseISO(exam.date), "dd/MM/yyyy"),
      exam.slotTime,
      exam.name,
      exam.code,
      exam.rooms.join("; "),
      exam.studentCount.toString(),
      exam.invigilators.join("; "),
    ]);

    const csvContent = [headers, ...rows].map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "exam-timetable.csv";
    a.click();
    toast.success("Timetable exported to CSV");
  };

  // Print view
  const handlePrint = () => {
    window.print();
    toast.success("Print dialog opened");
  };

  // Export to iCal
  const exportToICal = (exams: Exam[]) => {
    let icalContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Exam Timetable//EN
`;

    exams.forEach((exam) => {
      const startTime = exam.slotTime.split(" - ")[0].replace(":", "");
      const endTime = exam.slotTime.split(" - ")[1].replace(":", "");
      const dateFormatted = exam.date.replace(/-/g, "");

      icalContent += `BEGIN:VEVENT
DTSTART:${dateFormatted}T${startTime}00
DTEND:${dateFormatted}T${endTime}00
SUMMARY:${exam.name} (${exam.code})
LOCATION:${exam.rooms.join(", ")}
DESCRIPTION:Students: ${exam.studentCount}. Invigilators: ${exam.invigilators.join(", ")}
END:VEVENT
`;
    });

    icalContent += "END:VCALENDAR";

    const blob = new Blob([icalContent], { type: "text/calendar" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "exams.ics";
    a.click();
    toast.success("Calendar file downloaded");
  };

  const nextExam = getNextExam();
  const daysUntilNext = nextExam ? differenceInDays(parseISO(nextExam.date), new Date()) : null;

  // Render main content
  const content = !timetableData ? (
    <div className="flex-1 p-4 sm:p-6 lg:p-8 overflow-auto">
      <div className="max-w-md mx-auto mt-12">
        <Card>
          <CardHeader className="text-center">
            <CardTitle className="flex items-center justify-center gap-2">
              <CalendarIcon className="h-6 w-6 text-primary" />
              Exam Timetable Viewer
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Enter a timetable code or upload a JSON file to view the exam schedule
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Timetable Code</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Enter code (e.g., ABC123)"
                  value={timetableCode}
                  onChange={(e) => setTimetableCode(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleLoadTimetable()}
                />
                <Button onClick={handleLoadTimetable} disabled={isLoading}>
                  {isLoading ? "Loading..." : "View"}
                </Button>
              </div>
            </div>

            <div className="relative">
              <Separator />
              <span className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-2 text-xs text-muted-foreground">
                or
              </span>
            </div>

            <div className="space-y-2">
              <Label>Upload JSON File</Label>
              <div className="flex gap-2">
                <Input
                  type="file"
                  accept=".json"
                  onChange={handleFileUpload}
                  className="cursor-pointer"
                />
              </div>
            </div>

            <p className="text-xs text-muted-foreground text-center mt-4">
              Try code <strong>ABC123</strong> to load sample data
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  ) : (
    <div className="flex-1 p-4 sm:p-6 lg:p-8 overflow-auto">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">{timetableData.name}</h1>
            <p className="text-sm text-muted-foreground">
              {timetableData.institution} • {format(parseISO(timetableData.examPeriod.start), "MMM d")} - {format(parseISO(timetableData.examPeriod.end), "MMM d, yyyy")}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowHelpModal(true)}>
              <HelpCircle className="h-4 w-4 mr-1" /> Help
            </Button>
            <Button variant="outline" size="sm" onClick={handlePrint}>
              <Printer className="h-4 w-4 mr-1" /> Print
            </Button>
            <Button variant="outline" size="sm" onClick={() => exportToCSV(getFilteredExams())}>
              <FileDown className="h-4 w-4 mr-1" /> CSV
            </Button>
          </div>
        </div>

        {/* Next Exam Countdown */}
        {nextExam && daysUntilNext !== null && (
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-primary/10">
                  <Timer className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-muted-foreground">Next Exam</p>
                  <p className="font-semibold text-foreground">{nextExam.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {format(parseISO(nextExam.date), "EEEE, MMM d")} • {nextExam.slotTime}
                  </p>
                </div>
                <Badge variant={daysUntilNext <= 1 ? "destructive" : "secondary"} className="text-lg px-4 py-2">
                  {daysUntilNext === 0 ? "Today" : daysUntilNext === 1 ? "Tomorrow" : `${daysUntilNext} days`}
                </Badge>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Teacher Name Input */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <User className="h-5 w-5 text-muted-foreground" />
              <div className="flex-1 space-y-1">
                <Label className="text-sm font-medium">Your Name (for "My Exams" filter)</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter your name (e.g., Mr. Johnson)"
                    value={teacherName}
                    onChange={(e) => setTeacherName(e.target.value)}
                    className="max-w-xs"
                  />
                  <Button variant="secondary" size="sm" onClick={() => saveTeacherName(teacherName)}>
                    Save
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="myexams">My Exams</TabsTrigger>
            <TabsTrigger value="bydate">By Date</TabsTrigger>
            <TabsTrigger value="search">Search</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-4 flex items-center gap-3">
                  <BookOpen className="h-8 w-8 text-primary" />
                  <div>
                    <p className="text-2xl font-bold">{timetableData.exams.length}</p>
                    <p className="text-sm text-muted-foreground">Total Exams</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 flex items-center gap-3">
                  <MapPin className="h-8 w-8 text-blue-500" />
                  <div>
                    <p className="text-2xl font-bold">{timetableData.rooms.length}</p>
                    <p className="text-sm text-muted-foreground">Exam Rooms</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 flex items-center gap-3">
                  <Users className="h-8 w-8 text-green-500" />
                  <div>
                    <p className="text-2xl font-bold">
                      {timetableData.exams.reduce((sum, e) => sum + e.studentCount, 0)}
                    </p>
                    <p className="text-sm text-muted-foreground">Total Students</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Grid View */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Exam Schedule Grid</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="w-full">
                  <div className="min-w-[600px]">
                    <div className="grid grid-cols-1 gap-4">
                      {timetableData.slots.map((slot) => (
                        <div key={slot.name} className="space-y-2">
                          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                            <Clock className="h-4 w-4" />
                            {slot.name} ({slot.time})
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                            {timetableData.exams
                              .filter((exam) => exam.slot === slot.name)
                              .map((exam) => {
                                const examDate = parseISO(exam.date);
                                const isUpcoming = isToday(examDate) || isTomorrow(examDate);
                                return (
                                  <Card
                                    key={exam.id}
                                    className={`cursor-pointer hover:shadow-md transition-shadow ${
                                      isToday(examDate) ? "ring-2 ring-primary" : ""
                                    }`}
                                    onClick={() => {
                                      setSelectedExam(exam);
                                      setShowExamModal(true);
                                    }}
                                  >
                                    <CardContent className="p-3">
                                      <div className="flex items-start justify-between">
                                        <div className="space-y-1">
                                          <p className="font-medium text-sm">{exam.name}</p>
                                          <p className="text-xs text-muted-foreground">{exam.code}</p>
                                        </div>
                                        {isUpcoming && (
                                          <Badge variant={isToday(examDate) ? "destructive" : "secondary"} className="text-xs">
                                            {isToday(examDate) ? "Today" : "Tomorrow"}
                                          </Badge>
                                        )}
                                      </div>
                                      <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                                        <div className="flex items-center gap-1">
                                          <CalendarIcon className="h-3 w-3" />
                                          {format(examDate, "EEE, MMM d")}
                                        </div>
                                        <div className="flex items-center gap-1">
                                          <MapPin className="h-3 w-3" />
                                          {exam.rooms.join(", ")}
                                        </div>
                                        <div className="flex items-center gap-1">
                                          <Users className="h-3 w-3" />
                                          {exam.studentCount} students
                                        </div>
                                      </div>
                                    </CardContent>
                                  </Card>
                                );
                              })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* My Exams Tab */}
          <TabsContent value="myexams" className="space-y-4">
            {!teacherName ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <User className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-medium mb-2">Enter Your Name</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Please enter your name above to see your assigned exams
                  </p>
                </CardContent>
              </Card>
            ) : getMyExams().length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
                  <h3 className="font-medium mb-2">No Exams Assigned</h3>
                  <p className="text-sm text-muted-foreground">
                    You don't have any invigilation duties for "{teacherName}"
                  </p>
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <h3 className="font-medium">
                    Your Invigilation Schedule ({getMyExams().length} exams)
                  </h3>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => exportToCSV(getMyExams())}>
                      <FileDown className="h-4 w-4 mr-1" /> Export CSV
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => exportToICal(getMyExams())}>
                      <Download className="h-4 w-4 mr-1" /> Add to Calendar
                    </Button>
                  </div>
                </div>
                <div className="space-y-3">
                  {getMyExams().map((exam) => (
                    <Card
                      key={exam.id}
                      className="cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => {
                        setSelectedExam(exam);
                        setShowExamModal(true);
                      }}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="text-center min-w-[60px]">
                              <p className="text-2xl font-bold text-primary">
                                {format(parseISO(exam.date), "d")}
                              </p>
                              <p className="text-xs text-muted-foreground uppercase">
                                {format(parseISO(exam.date), "MMM")}
                              </p>
                            </div>
                            <div>
                              <p className="font-medium">{exam.name}</p>
                              <p className="text-sm text-muted-foreground">
                                {exam.slot} • {exam.slotTime}
                              </p>
                              <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <MapPin className="h-3 w-3" /> {exam.rooms.join(", ")}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Users className="h-3 w-3" /> {exam.studentCount}
                                </span>
                              </div>
                            </div>
                          </div>
                          <ChevronRight className="h-5 w-5 text-muted-foreground" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </>
            )}
          </TabsContent>

          {/* By Date Tab */}
          <TabsContent value="bydate" className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="justify-start">
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    {selectedDate ? format(selectedDate, "PPP") : "Select a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              {selectedDate && (
                <Button variant="ghost" size="sm" onClick={() => setSelectedDate(undefined)}>
                  Clear Filter
                </Button>
              )}
            </div>

            {selectedDate ? (
              getFilteredExams().length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center">
                    <CalendarIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="font-medium mb-2">No Exams on This Date</h3>
                    <p className="text-sm text-muted-foreground">
                      There are no exams scheduled for {format(selectedDate, "MMMM d, yyyy")}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {getFilteredExams().map((exam) => (
                    <Card
                      key={exam.id}
                      className="cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => {
                        setSelectedExam(exam);
                        setShowExamModal(true);
                      }}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{exam.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {exam.slot} • {exam.slotTime}
                            </p>
                            <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" /> {exam.rooms.join(", ")}
                              </span>
                              <span className="flex items-center gap-1">
                                <Users className="h-3 w-3" /> {exam.studentCount} students
                              </span>
                            </div>
                          </div>
                          <ChevronRight className="h-5 w-5 text-muted-foreground" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )
            ) : (
              <Card>
                <CardContent className="p-8 text-center">
                  <CalendarIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-medium mb-2">Select a Date</h3>
                  <p className="text-sm text-muted-foreground">
                    Choose a date from the calendar to view exams scheduled for that day
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Search Tab */}
          <TabsContent value="search" className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search exams, rooms, or invigilators..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <select
                className="px-3 py-2 border rounded-md bg-background text-sm"
                value={filterRoom}
                onChange={(e) => setFilterRoom(e.target.value)}
              >
                <option value="all">All Rooms</option>
                {timetableData.rooms.map((room) => (
                  <option key={room} value={room}>{room}</option>
                ))}
              </select>
              <select
                className="px-3 py-2 border rounded-md bg-background text-sm"
                value={filterDepartment}
                onChange={(e) => setFilterDepartment(e.target.value)}
              >
                <option value="all">All Departments</option>
                {getDepartments().map((dept) => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>

            {getFilteredExams().length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Search className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-medium mb-2">No Results Found</h3>
                  <p className="text-sm text-muted-foreground">
                    Try adjusting your search or filters
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Found {getFilteredExams().length} exam(s)
                </p>
                {getFilteredExams().map((exam) => (
                  <Card
                    key={exam.id}
                    className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => {
                      setSelectedExam(exam);
                      setShowExamModal(true);
                    }}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="text-center min-w-[60px]">
                            <p className="text-2xl font-bold text-primary">
                              {format(parseISO(exam.date), "d")}
                            </p>
                            <p className="text-xs text-muted-foreground uppercase">
                              {format(parseISO(exam.date), "MMM")}
                            </p>
                          </div>
                          <div>
                            <p className="font-medium">{exam.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {exam.code} • {exam.slot} • {exam.slotTime}
                            </p>
                            <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" /> {exam.rooms.join(", ")}
                              </span>
                              <span className="flex items-center gap-1">
                                <Users className="h-3 w-3" /> {exam.studentCount}
                              </span>
                            </div>
                          </div>
                        </div>
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Exam Details Modal */}
      <Dialog open={showExamModal} onOpenChange={setShowExamModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedExam?.name}</DialogTitle>
          </DialogHeader>
          {selectedExam && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Code</p>
                  <p className="font-medium">{selectedExam.code}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Department</p>
                  <p className="font-medium">{selectedExam.department || "-"}</p>
                </div>
              </div>
              <Separator />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Date</p>
                  <p className="font-medium">{format(parseISO(selectedExam.date), "EEEE, MMM d, yyyy")}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Time</p>
                  <p className="font-medium">{selectedExam.slot} ({selectedExam.slotTime})</p>
                </div>
              </div>
              <Separator />
              <div>
                <p className="text-xs text-muted-foreground">Room(s)</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {selectedExam.rooms.map((room) => (
                    <Badge key={room} variant="secondary">{room}</Badge>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Number of Students</p>
                <p className="font-medium">{selectedExam.studentCount}</p>
              </div>
              <Separator />
              <div>
                <p className="text-xs text-muted-foreground">Invigilators</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {selectedExam.invigilators.map((inv) => (
                    <Badge key={inv} variant="outline">{inv}</Badge>
                  ))}
                </div>
              </div>
              {selectedExam.notes && (
                <div>
                  <p className="text-xs text-muted-foreground">Notes</p>
                  <p className="text-sm">{selectedExam.notes}</p>
                </div>
              )}
              {selectedExam.specialRequirements && (
                <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-md">
                  <p className="text-xs text-yellow-600 dark:text-yellow-400 font-medium flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" /> Special Requirements
                  </p>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">{selectedExam.specialRequirements}</p>
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <Button variant="outline" size="sm" className="flex-1" onClick={() => exportToICal([selectedExam])}>
                  <Download className="h-4 w-4 mr-1" /> Add to Calendar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Help Modal */}
      <Dialog open={showHelpModal} onOpenChange={setShowHelpModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>How to Use This Viewer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div>
              <h4 className="font-medium mb-1">Overview Tab</h4>
              <p className="text-muted-foreground">See all exams in a grid view organized by time slots. Click any exam for full details.</p>
            </div>
            <div>
              <h4 className="font-medium mb-1">My Exams Tab</h4>
              <p className="text-muted-foreground">Enter your name at the top to filter and see only the exams you're assigned to invigilate.</p>
            </div>
            <div>
              <h4 className="font-medium mb-1">By Date Tab</h4>
              <p className="text-muted-foreground">Select a specific date to see all exams scheduled for that day.</p>
            </div>
            <div>
              <h4 className="font-medium mb-1">Search Tab</h4>
              <p className="text-muted-foreground">Search by exam name, code, room, or teacher name. Use filters to narrow results.</p>
            </div>
            <Separator />
            <div>
              <h4 className="font-medium mb-1">Exporting</h4>
              <p className="text-muted-foreground">Use the Print, CSV, or Calendar buttons to export the timetable for offline use.</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );

  return (
    <SidebarProvider>
      <div className="flex min-h-screen bg-gray-50/50 dark:bg-gray-900 w-full font-sans">
        <Teachersidebar />
        <main className="flex-1 flex flex-col w-full h-screen overflow-hidden">
          <Teacherheader />
          {content}
        </main>
      </div>
    </SidebarProvider>
  );
};

export default TeacherExamViewer;